/**
 * « Se tester » sur un pack (migration 0336) — sur « Mon année », chaque pack
 * propose un test : 2 questions par SKILLCARD du pack (15 au plus), résultat
 * par carte. On évalue : XP seulement (pas d'HORA ni de maîtrise). PAYANT :
 * toutes les cartes du pack actives, ou abonnement / pass. Client des RPC +
 * helpers PURS (testés).
 */

import { publishProgress } from '@/lib/progress-sync';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { AutomatismeQuestion } from '@/lib/automatismes';

const db = supabase as unknown as SupabaseClient;

export interface PackTestSession {
  id: string;
  pack_id: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  question_count: number;
  answered_count: number;
  started_at: string;
  completed_at: string | null;
  total: number | null;
  correct: number | null;
  score_pct: number | null;
  xp_awarded: number;
}

export type PackTestAccess = 'admin' | 'subscription' | 'pass' | 'owned' | null;

export interface PackTestState {
  pack_id: string;
  card_count: number;
  first_card_id: string | null;
  access: PackTestAccess;
  in_progress: PackTestSession | null;
  last: PackTestSession | null;
}

export interface PackTestPlay {
  session: PackTestSession;
  questions: AutomatismeQuestion[];
  answered: { question_id: string; was_correct: boolean }[];
}

export interface PackTestCardResult {
  card_id: string;
  correct: number;
  total: number;
  title: string | null;
  levels: string[];
  color_theme: string | null;
  creature: unknown;
}

export interface PackTestResult {
  session: PackTestSession;
  pack: { id: string; title: string } | null;
  cards: PackTestCardResult[];
}

// ── Helpers purs ────────────────────────────────────────────────────────────

/** Ce que montre le pack sur « Mon année ». */
export type PackTestAction =
  | { kind: 'none' }
  | { kind: 'test'; label: string; detail: string | null }
  | { kind: 'locked'; cardId: string | null };

export function packTestAction(state: PackTestState | null | undefined): PackTestAction {
  if (!state || state.card_count === 0) return { kind: 'none' };
  if (!state.access) return { kind: 'locked', cardId: state.first_card_id };
  const cur = state.in_progress;
  if (cur) {
    return {
      kind: 'test',
      label: `Reprendre le test (${cur.answered_count}/${cur.question_count})`,
      detail: null,
    };
  }
  const last = state.last;
  return {
    kind: 'test',
    label: last ? 'M’entraîner à nouveau' : 'S’entraîner',
    detail: last?.score_pct != null ? `Dernier test : ${last.score_pct} %` : null,
  };
}

/** Une carte est « à retravailler » après le test si elle n'est pas réussie à 100 %. */
export function packTestCardsToRework(cards: readonly PackTestCardResult[]): PackTestCardResult[] {
  return cards.filter((c) => c.correct < c.total);
}

export function packTestErrorMessage(code: string): string {
  switch (code) {
    case 'LOCKED':
      return 'Le test de ce pack est réservé aux élèves qui ont débloqué ses SKILLCARDS.';
    case 'NO_QUESTIONS_AVAILABLE':
      return 'Pas encore assez de questions pour tester ce pack. Reviens bientôt !';
    case 'PACK_NOT_FOUND':
      return 'Ce pack n’existe plus.';
    case 'NO_ANSWERS':
      return 'Réponds au moins à une question avant de terminer.';
    default:
      return 'Une erreur est survenue. Réessaie.';
  }
}

export class PackTestError extends Error {
  constructor(
    public readonly code: string,
    public readonly cardId: string | null = null,
  ) {
    super(code);
    this.name = 'PackTestError';
  }
}

function unwrap<T>(data: unknown, error: { message: string } | null): T {
  if (error) throw new PackTestError('NETWORK_ERROR');
  if (data && typeof data === 'object' && 'error' in data) {
    const d = data as { error: string; card_id?: string | null };
    throw new PackTestError(String(d.error), d.card_id ?? null);
  }
  return data as T;
}

// ── RPC + hooks ─────────────────────────────────────────────────────────────

export const packTestsStateKey = (packIds: readonly string[]) =>
  ['pack-tests-state', [...packIds].sort().join(',')] as const;

export function usePackTestsState(packIds: readonly string[]) {
  return useQuery({
    queryKey: packTestsStateKey(packIds),
    enabled: packIds.length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<Record<string, PackTestState>> => {
      const { data, error } = await db.rpc('get_pack_tests_state', { p_pack_ids: packIds });
      const rows = unwrap<PackTestState[]>(data, error) ?? [];
      return Object.fromEntries(rows.map((r) => [r.pack_id, r]));
    },
  });
}

export async function startPackTest(packId: string, restart = false): Promise<PackTestPlay> {
  const { data, error } = await db.rpc('start_pack_test', {
    p_pack_id: packId,
    p_restart: restart,
  });
  return unwrap<PackTestPlay>(data, error);
}

export async function submitPackTestAnswer(input: {
  testId: string;
  questionId: string;
  choiceId: string;
  timeMs: number;
}): Promise<{ was_correct: boolean; explanation_md: string | null; feedback_md: string | null }> {
  const { data, error } = await db.rpc('submit_pack_test_answer', {
    p_test_id: input.testId,
    p_question_id: input.questionId,
    p_choice_id: input.choiceId,
    p_time_ms: input.timeMs,
  });
  return unwrap(data, error);
}

export async function finishPackTest(testId: string): Promise<PackTestResult> {
  const { data, error } = await db.rpc('finish_pack_test', { p_test_id: testId });
  const result = unwrap<PackTestResult>(data, error);
  publishProgress('session-finished');
  return result;
}
